import React, { useState, useEffect } from 'react';
import { 
  FaCogs, 
  FaPlus, 
  FaTrash, 
  FaBuilding, 
  FaProjectDiagram, 
  FaLayerGroup, 
  FaSitemap, 
  FaWallet, 
  FaChartBar, 
  FaMoneyBillWave, 
  FaMapMarkerAlt,
  FaList,
  FaSpinner,
  FaClipboardList,
  FaFileInvoiceDollar,
  FaCommentDots,
  FaDatabase
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import axios from 'axios';
import { rootUrl, authHeaders } from '../../helpers';
import FormGenerator from '../../components/formGenerator';
import Modal from '../../components/Modal';
import { useAuth } from '../../context/auth-context';

export default function Settings() {
  const auth = useAuth();
  const [modal, setModal] = useState({ title: '', open: false, body: '', size: 'sm' });
  const [mainSection, setMainSection] = useState('workflow');
  const [masterTab, setMasterTab] = useState(0);

  let type = '';

  const [budget, setBudget] = useState([]);
  const [banks, setBanks] = useState([]);
  const [coa, setCoa] = useState([]);
  const [dept, setDept] = useState([]);
  const [program, setProgram] = useState([]);
  const [subProgram, setSubProgram] = useState([]);
  const [units, setUnits] = useState([]);
  const [councils, setCouncil] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [center, setCenter] = useState([]);
  const [output, setOutput] = useState([]); 
  const [requiresPvApproval, setRequiresPvApproval] = useState(true);
  const [savingApprovalSetting, setSavingApprovalSetting] = useState(false);
  const [bulkBillingPeriod, setBulkBillingPeriod] = useState('jan-jun');
  const [bulkBillingYear, setBulkBillingYear] = useState(new Date().getFullYear());
  const [runningBulkBilling, setRunningBulkBilling] = useState(false);
  const [smsPreview, setSmsPreview] = useState(null);
  const [loadingSmsPreview, setLoadingSmsPreview] = useState(false);
  const [sendingBulkSms, setSendingBulkSms] = useState(false);
  const [smsStatus, setSmsStatus] = useState(null);
  const [loadingSmsStatus, setLoadingSmsStatus] = useState(false);
  const [testSmsPhone, setTestSmsPhone] = useState('');
  const [testSmsMessage, setTestSmsMessage] = useState('Hello from Gramosi. This is a test SMS.');
  const [sendingTestSms, setSendingTestSms] = useState(false);

  const handleClose = () => {
    setModal({ ...modal, open: false });
  };

  const fetchData = async () => {
    try {
      const { data } = await axios.get(rootUrl(`/getProjects/${auth.user.id}`));
      setBudget(data.data.budget);
      setCoa(data.data.coa);
      setDept(data.data.dept);
      setProgram(data.data.program);
      setSubProgram(data.data.subProgram);
      setOutput(data.data.subProgram);
      setUnits(data.data.units);
      setCouncil(data.data.councils);
      setBanks(data.data.banks);
      setExpenses(data.data.expenses);
      setCenter(data.data.center);

      const currentCouncil = (data.data.councils || []).find(
        (c) => String(c.code) === String(auth.user.cid)
      );
      if (currentCouncil && currentCouncil.pv_requires_approval !== undefined) {
        setRequiresPvApproval(Number(currentCouncil.pv_requires_approval) === 1);
      } else {
        setRequiresPvApproval(true);
      }
    } catch (e) {
      toast.error(e?.message);
    }
  };

  const fetchSmsStatus = async () => {
    if (!auth.user?.cid) return;
    try {
      setLoadingSmsStatus(true);
      const response = await axios.get(rootUrl(`/councilSmsStatus/${auth.user.cid}`), { headers: authHeaders() });
      if (response?.data?.status === 200) {
        setSmsStatus(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load SMS status:', error);
    } finally {
      setLoadingSmsStatus(false);
    }
  };

  useEffect(() => {
    if (auth.user != null) {
      fetchData();
      fetchSmsStatus();
    }
  }, []);

  const submitSetting = async (data) => {
    data['type'] = type;
    data['c_id'] = auth.user.cid;
    try {
      const response = await axios.post(rootUrl('/settings'), data);
      if (response.data.status === 200) {
        toast.success('Submitted successfully');
        fetchData();
        setModal({ ...modal, open: false });
      } else {
        toast.error(response.data.message);
      }
    } catch (e) {
      toast.error(e?.message);
    }
  };

  const addSett = (name) => {
    type = name;
    let fields = [{ type: 'text', name: 'name', label: 'Name' }];

    switch (name) {
      case 'council':
        fields = [{ type: 'text', name: 'code', label: 'Code' }, { type: 'text', name: 'name', label: 'Name' }];
        break;
      case 'bank':
          fields = [{ type: 'text', name: 'code', label: 'Code' }, { type: 'text', name: 'account_no', label: 'Account #' }, { type: 'text', name: 'name', label: 'Name' }];
          break;
      case 'expense':
          fields = [{ type: 'text', name: 'name', label: 'Name' }];
          break;
      case 'center':
            fields = [{ type: 'text', name: 'collection_name', label: 'Name' }];
            break;
              
      case 'department':
        fields = [{ type: 'text', name: 'id', label: 'Id' }, { type: 'text', name: 'name', label: 'Name' }];
        break;
      case 'program':
        fields = [{ type: 'text', name: 'programCode', label: 'Code' }, { type: 'text', name: 'programName', label: 'Name' }];
        break;
      case 'sub':
        fields = [
          { type: 'text', name: 'subProgramCode', label: 'Code' },
          { type: 'text', name: 'name', label: 'Name' },
          {
            type: 'select',
            name: 'programCode',
            label: 'Program',
            required: true,
            options: program.map((res) => ({ value: res.programCode, label: res.name })),
          },
        ];
        break;
      case 'unit':
        fields = [
          { type: 'text', name: 'name', label: 'Name' },
          {
            type: 'select',
            name: 'departmntCode',
            label: 'Department',
            required: true,
            options: dept.map((res) => ({ value: res.id, label: res.name })),
          },
        ];
        break;
      case 'output':
        fields = [
            { type: 'text', name: 'indicators', label: 'Indicator' },
            { type: 'text', name: 'keyIndicators', label: 'Key Indicator' },
            {
              type: 'select',
              name: 'programCode',
              label: 'Program',
              required: true,
              options: program.map((res) => ({ value: res.programCode, label: res.name })),
            },
          ];
        break;
      default:
        break;
    }

    setModal({
      title: 'Add ' + name,
      open: true,
      size: 'sm',
      body: <FormGenerator fields={fields} onSubmit={submitSetting} />,
    });
  };

  const savePaymentApprovalSetting = async () => {
    setSavingApprovalSetting(true);
    try {
      const response = await axios.post(rootUrl('/updateCouncilPaymentApproval'), {
        cid: auth.user.cid,
        uid: auth.user.id,
        requiresApproval: requiresPvApproval ? 1 : 0,
      });

      if (response.data.status === 200) {
        toast.success('Payment approval setting saved');
        fetchData();
      } else {
        toast.error(response.data.message || 'Failed to save setting');
      }
    } catch (e) {
      toast.error(e?.message);
    } finally {
      setSavingApprovalSetting(false);
    }
  };

  const loadBulkBillSmsPreview = async () => {
    const yearNumber = Number(bulkBillingYear);
    if (!Number.isInteger(yearNumber) || yearNumber < 2000 || yearNumber > 2100) {
      toast.error('Please provide a valid billing year');
      return;
    }

    try {
      setLoadingSmsPreview(true);
      setSmsPreview(null);
      const payload = `${auth.user.cid}~${bulkBillingPeriod}~${yearNumber}`;
      const response = await axios.get(rootUrl(`/bulkBillSmsPreview/${payload}`), { headers: authHeaders() });
      if (response?.data?.status === 200) {
        setSmsPreview(response.data.data);
        return;
      }
      toast.error(response?.data?.message || 'Failed to load SMS preview');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to load SMS preview');
    } finally {
      setLoadingSmsPreview(false);
    }
  };

  const sendTestSms = async () => {
    if (!testSmsPhone.trim() || !testSmsMessage.trim()) {
      toast.error('Phone number and message are required');
      return;
    }
    if (!smsStatus?.has_balance) {
      toast.error('No SMS balance available. Contact your system administrator.');
      return;
    }

    try {
      setSendingTestSms(true);
      const response = await axios.post(
        rootUrl('/councilSmsTest'),
        {
          cid: auth.user.cid,
          phone: testSmsPhone.trim(),
          message: testSmsMessage.trim(),
        },
        { headers: authHeaders() }
      );
      if (response?.data?.status === 200) {
        toast.success(response.data.message || 'Test SMS sent');
        if (response.data.data) {
          setSmsStatus((prev) => ({ ...prev, ...response.data.data }));
        }
        await fetchSmsStatus();
        return;
      }
      toast.error(response?.data?.message || 'Failed to send test SMS');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to send test SMS');
    } finally {
      setSendingTestSms(false);
    }
  };

  const sendBulkBillSms = async () => {
    if (!smsPreview?.summary?.bills_with_phone) {
      toast.error('Load preview first. No bills with phone numbers to message.');
      return;
    }
    if (!smsPreview?.sms_ready) {
      toast.error('Zamtel SMS is not configured on the server');
      return;
    }
    if (!smsPreview?.has_sufficient_balance) {
      toast.error(`Insufficient SMS balance. Need ${smsPreview.sms_required}, have ${smsPreview.sms_balance}.`);
      return;
    }

    const confirmed = window.confirm(
      `Send ${smsPreview.summary.bills_with_phone} SMS for ${smsPreview.period_label} ${smsPreview.year}? This will use ${smsPreview.sms_required} SMS credit(s).`
    );
    if (!confirmed) return;

    try {
      setSendingBulkSms(true);
      const response = await axios.post(
        rootUrl('/bulkBillSmsSend'),
        {
          cid: auth.user.cid,
          period: bulkBillingPeriod,
          year: Number(bulkBillingYear),
        },
        { headers: authHeaders() }
      );
      if (response?.data?.status === 200) {
        toast.success(response.data.message || 'SMS sent');
        await loadBulkBillSmsPreview();
        await fetchSmsStatus();
        return;
      }
      toast.error(response?.data?.message || 'Failed to send SMS');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to send SMS');
    } finally {
      setSendingBulkSms(false);
    }
  };

  const runBulkBilling = async () => {
    const yearNumber = Number(bulkBillingYear);
    if (!Number.isInteger(yearNumber) || yearNumber < 2000 || yearNumber > 2100) {
      toast.error('Please provide a valid billing year');
      return;
    }

    if (!['jan-jun', 'jul-dec'].includes(bulkBillingPeriod)) {
      toast.error('Please select a valid billing period');
      return;
    }

    try {
      setRunningBulkBilling(true);
      const payload = `${auth.user.cid}~${bulkBillingPeriod}~${yearNumber}`;
      const response = await axios.get(rootUrl(`/bulkBill/${payload}`), { headers: authHeaders() });
      if (response?.data?.status === 200) {
        toast.success(response.data.message || 'Bulk billing completed successfully');
        return;
      }
      toast.error(response?.data?.message || 'Bulk billing failed');
    } catch (error) {
      const message = error?.response?.data?.message || 'Failed to run bulk billing';
      toast.error(message);
    } finally {
      setRunningBulkBilling(false);
    }
  };

  const mainSections = [
    { id: 'workflow', label: 'Workflow', icon: FaClipboardList },
    { id: 'billing', label: 'Property Billing', icon: FaFileInvoiceDollar },
    { id: 'sms', label: 'SMS', icon: FaCommentDots },
    { id: 'master', label: 'Master Data', icon: FaDatabase },
  ];

  const masterTabData = [
    { name: 'Departments', list: dept, addFunc: 'department', icon: FaBuilding },
    { name: 'Programs', list: program, addFunc: 'program', icon: FaProjectDiagram },
    { name: 'SubPrograms', list: subProgram, addFunc: 'sub', icon: FaLayerGroup },
    { name: 'Units', list: units, addFunc: 'unit', icon: FaSitemap },
    { name: 'Banks', list: banks, addFunc: 'bank', icon: FaWallet },
    { name: 'Outputs', list: output, addFunc: 'output', icon: FaChartBar },
    { name: 'Expense Type', list: expenses, addFunc: 'expense', icon: FaMoneyBillWave },
    { name: 'Collection Center', list: center, addFunc: 'center', icon: FaMapMarkerAlt },
  ];

  const periodYearFields = (
    <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
      <div>
        <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">Period</label>
        <select
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={bulkBillingPeriod}
          onChange={(event) => setBulkBillingPeriod(event.target.value)}
          disabled={runningBulkBilling || sendingBulkSms || loadingSmsPreview}
        >
          <option value="jan-jun">January - June</option>
          <option value="jul-dec">July - December</option>
        </select>
      </div>
      <div>
        <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">Year</label>
        <input
          type="number"
          min="2000"
          max="2100"
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-32 focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={bulkBillingYear}
          onChange={(event) => setBulkBillingYear(event.target.value)}
          disabled={runningBulkBilling || sendingBulkSms || loadingSmsPreview}
        />
      </div>
    </div>
  );

  const renderWorkflowSection = () => (
    <div className="p-6">
      <h3 className="font-bold text-lg text-gray-900">Payment Voucher Workflow</h3>
      <p className="text-sm text-gray-600 mt-1">
        Configure whether new payment vouchers pass through online approval or post directly.
      </p>
      <div className="mt-4 flex flex-col gap-3">
        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
          <input type="radio" name="pvApprovalMode" checked={requiresPvApproval} onChange={() => setRequiresPvApproval(true)} />
          Use online approval
        </label>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
          <input type="radio" name="pvApprovalMode" checked={!requiresPvApproval} onChange={() => setRequiresPvApproval(false)} />
          Skip online approval
        </label>
      </div>
      <div className="mt-4">
        <button
          onClick={savePaymentApprovalSetting}
          disabled={savingApprovalSetting}
          className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-950 to-blue-800 text-white rounded-lg hover:shadow-lg transition-all font-medium disabled:opacity-60"
        >
          {savingApprovalSetting ? <FaSpinner className="animate-spin" /> : null}
          Save workflow setting
        </button>
      </div>
    </div>
  );

  const renderBillingSection = () => (
    <div className="p-6 space-y-6">
      <div>
        <h3 className="font-bold text-lg text-gray-900">Generate bills</h3>
        <p className="text-sm text-gray-600 mt-1">Run half-year property bills once per period.</p>
        <div className="mt-4 flex flex-col lg:flex-row lg:items-end gap-4">
          {periodYearFields}
          <button
            type="button"
            onClick={runBulkBilling}
            disabled={runningBulkBilling}
            className="inline-flex items-center justify-center gap-2 bg-blue-950 hover:bg-blue-900 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            {runningBulkBilling ? <FaSpinner className="animate-spin" /> : null}
            {runningBulkBilling ? 'Running...' : 'Run bulk billing'}
          </button>
        </div>
      </div>

      <div className="pt-6 border-t border-gray-200">
        <h3 className="font-bold text-lg text-gray-900">Billing SMS notifications</h3>
        <p className="text-sm text-gray-600 mt-1">
          Preview and send Zamtel SMS for property bills in the selected period.
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={loadBulkBillSmsPreview}
            disabled={loadingSmsPreview || sendingBulkSms}
            className="inline-flex items-center gap-2 border border-blue-950 text-blue-950 hover:bg-blue-50 disabled:opacity-50 px-4 py-2 rounded-lg text-sm font-medium"
          >
            {loadingSmsPreview ? <FaSpinner className="animate-spin" /> : null}
            {loadingSmsPreview ? 'Loading...' : 'Preview recipients'}
          </button>
          <button
            type="button"
            onClick={sendBulkBillSms}
            disabled={!smsPreview || sendingBulkSms || loadingSmsPreview || !smsPreview?.sms_ready || !smsPreview?.has_sufficient_balance || !smsStatus?.has_balance}
            className="inline-flex items-center gap-2 bg-green-700 hover:bg-green-800 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            {sendingBulkSms ? <FaSpinner className="animate-spin" /> : null}
            {sendingBulkSms ? 'Sending...' : 'Send billing SMS'}
          </button>
        </div>

        {smsPreview && (
          <div className="mt-4 space-y-4 max-h-[calc(100vh-22rem)] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <div className="rounded-lg bg-gray-50 border p-3"><p className="text-xs text-gray-500 uppercase">Customers</p><p className="text-lg font-bold">{smsPreview.summary.distinct_customers}</p></div>
              <div className="rounded-lg bg-green-50 border border-green-200 p-3"><p className="text-xs text-green-700 uppercase">With phone</p><p className="text-lg font-bold text-green-900">{smsPreview.summary.customers_with_phone}</p></div>
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3"><p className="text-xs text-amber-700 uppercase">No phone</p><p className="text-lg font-bold text-amber-900">{smsPreview.summary.customers_without_phone}</p></div>
              <div className="rounded-lg bg-gray-50 border p-3"><p className="text-xs text-gray-500 uppercase">Bills</p><p className="text-lg font-bold">{smsPreview.summary.total_bills}</p></div>
              <div className="rounded-lg bg-green-50 border border-green-200 p-3"><p className="text-xs text-green-700 uppercase">SMS ready</p><p className="text-lg font-bold text-green-900">{smsPreview.summary.bills_with_phone}</p></div>
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3"><p className="text-xs text-amber-700 uppercase">Skipped</p><p className="text-lg font-bold text-amber-900">{smsPreview.summary.bills_without_phone}</p></div>
            </div>
            {smsPreview.sms_ready && (
              <p className="text-sm text-gray-600">
                Sender ID: <strong>{smsPreview.sender_id}</strong> · Balance: <strong>{smsPreview.sms_balance}</strong> · Required: <strong>{smsPreview.sms_required}</strong>
              </p>
            )}
            {!smsPreview.sms_ready && <p className="text-sm text-red-600">Zamtel SMS is not configured on the server.</p>}
            {smsPreview.sms_ready && !smsPreview.has_sufficient_balance && (
              <p className="text-sm text-red-600">Insufficient SMS balance. Contact your system administrator.</p>
            )}
            {smsPreview.recipients?.length > 0 && (
              <div className="rounded-lg border border-gray-200 overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 text-sm font-medium text-gray-900">
                  SMS recipients ({smsPreview.recipients.filter((r) => r.has_phone).length} with phone)
                </div>
                <div className="overflow-x-auto max-h-64 overflow-y-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-white sticky top-0 border-b">
                      <tr className="text-left text-xs uppercase text-gray-500">
                        <th className="px-4 py-2 font-semibold">Customer</th>
                        <th className="px-4 py-2 font-semibold">Phone</th>
                        <th className="px-4 py-2 font-semibold">Invoice</th>
                        <th className="px-4 py-2 font-semibold">Property</th>
                        <th className="px-4 py-2 font-semibold text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {smsPreview.recipients.map((row) => (
                        <tr key={row.bill_id} className={row.has_phone ? 'bg-white' : 'bg-amber-50/50'}>
                          <td className="px-4 py-2 font-medium text-gray-900">{row.customer_name}</td>
                          <td className="px-4 py-2 text-gray-700">
                            {row.has_phone ? (row.phone_raw || row.phone) : <span className="text-amber-700">No phone</span>}
                          </td>
                          <td className="px-4 py-2 text-gray-600">{row.invoice_no || '—'}</td>
                          <td className="px-4 py-2 text-gray-600">{row.property_no || '—'}</td>
                          <td className="px-4 py-2 text-right text-gray-900">{Number(row.bill_amount).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const renderSmsSection = () => (
    <div className="p-6 space-y-6 max-h-[calc(100vh-14rem)] overflow-y-auto">
      <div>
        <h3 className="font-bold text-lg text-gray-900">SMS wallet</h3>
        <p className="text-sm text-gray-600 mt-1">
          Council <strong>{auth.user?.cid}</strong> uses sender ID <strong>{smsStatus?.sender_id || '—'}</strong> (9803 → gwembetc, others → Gramosi).
        </p>
        {loadingSmsStatus && !smsStatus ? (
          <div className="mt-4 flex items-center gap-2 text-sm text-gray-600"><FaSpinner className="animate-spin" /> Loading...</div>
        ) : smsStatus ? (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-lg bg-blue-50 border border-blue-200 p-3"><p className="text-xs text-blue-700 uppercase">Balance</p><p className="text-2xl font-bold text-blue-950">{smsStatus.balance}</p></div>
            <div className="rounded-lg bg-gray-50 border p-3"><p className="text-xs text-gray-500 uppercase">Purchased</p><p className="text-lg font-bold">{smsStatus.messages_purchased_total}</p></div>
            <div className="rounded-lg bg-gray-50 border p-3"><p className="text-xs text-gray-500 uppercase">Sent</p><p className="text-lg font-bold">{smsStatus.messages_sent_total}</p></div>
            <div className="rounded-lg bg-gray-50 border p-3"><p className="text-xs text-gray-500 uppercase">Sender ID</p><p className="text-lg font-bold">{smsStatus.sender_id}</p></div>
          </div>
        ) : null}
        {!smsStatus?.sms_ready && <p className="mt-3 text-sm text-red-600">Zamtel API not configured (ZAMTEL_SMS_ENABLED, ZAMTEL_API_KEY).</p>}
        {smsStatus && !smsStatus.has_balance && (
          <p className="mt-3 text-sm text-amber-700">No balance available. Contact your system administrator to top up SMS credits.</p>
        )}
      </div>

      <div className="rounded-lg border border-gray-200 p-4">
        <h4 className="font-medium text-gray-900">Send test SMS</h4>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">Phone</label>
            <input type="text" className="border rounded-lg px-3 py-2 text-sm w-full" value={testSmsPhone} onChange={(e) => setTestSmsPhone(e.target.value)} disabled={sendingTestSms} placeholder="0971234567" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">Message</label>
            <textarea rows={3} className="border rounded-lg px-3 py-2 text-sm w-full" value={testSmsMessage} onChange={(e) => setTestSmsMessage(e.target.value)} disabled={sendingTestSms} />
          </div>
        </div>
        <button type="button" onClick={sendTestSms} disabled={sendingTestSms || !smsStatus?.has_balance || !smsStatus?.sms_ready} className="mt-3 inline-flex items-center gap-2 border border-green-700 text-green-700 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
          {sendingTestSms ? <FaSpinner className="animate-spin" /> : null}
          Send test SMS
        </button>
      </div>

      {smsStatus?.recent_messages?.length > 0 && (
        <div className="rounded-lg border overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 text-sm font-medium">Recent messages</div>
          <div className="max-h-56 overflow-y-auto divide-y">
            {smsStatus.recent_messages.map((row) => (
              <div key={row.id} className="px-4 py-3 text-sm">
                <div className="flex justify-between gap-2">
                  <span className="font-medium">{row.phone}</span>
                  <span className={`text-xs uppercase ${row.status === 'sent' ? 'text-green-700' : 'text-red-600'}`}>{row.status} · {row.message_type}</span>
                </div>
                <p className="text-gray-600 mt-1 whitespace-pre-wrap line-clamp-2">{row.message}</p>
                <p className="text-xs text-gray-400 mt-1">{row.created_at}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderMasterSection = () => {
    const tab = masterTabData[masterTab];
    const Icon = tab?.icon || FaList;
    return (
      <div className="flex flex-col min-h-[calc(100vh-14rem)]">
        <div className="flex flex-wrap gap-2 p-4 border-b border-gray-200 bg-gray-50">
          {masterTabData.map((item, index) => {
            const TabIcon = item.icon || FaList;
            return (
              <button
                key={item.name}
                type="button"
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  masterTab === index
                    ? 'bg-gradient-to-r from-blue-950 to-blue-800 text-white shadow-md'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
                onClick={() => setMasterTab(index)}
              >
                <TabIcon className={masterTab === index ? 'text-white' : 'text-gray-500'} />
                <span>{item.name}</span>
              </button>
            );
          })}
        </div>
        {tab && (
          <div className="flex-1 overflow-hidden">
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 rounded-lg p-2"><Icon className="text-blue-600 text-xl" /></div>
                <div>
                  <h3 className="font-bold text-lg">{tab.name}</h3>
                  <p className="text-sm text-gray-500">{tab.list.length} {tab.list.length === 1 ? 'item' : 'items'}</p>
                </div>
              </div>
              <button type="button" onClick={() => addSett(tab.addFunc)} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-950 to-blue-800 text-white rounded-lg text-sm font-medium">
                <FaPlus /><span>Add {tab.name.slice(0, -1)}</span>
              </button>
            </div>
            <div className="max-h-[calc(100vh-20rem)] overflow-y-auto">
              {tab.list.length > 0 ? (
                <div className="divide-y">
                  {tab.list.map((entry, idx) => (
                    <div key={idx} className="flex justify-between items-center px-6 py-4 hover:bg-gray-50 group">
                      <div className="flex items-center gap-4 flex-1">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 font-semibold text-sm">{idx + 1}</div>
                        <div>
                          <p className="font-medium">{entry.name || entry.programName || entry.collection_name || 'N/A'}</p>
                          {(entry.code || entry.programCode || entry.subProgramCode) && (
                            <p className="text-sm text-gray-500">Code: {entry.code || entry.programCode || entry.subProgramCode}</p>
                          )}
                        </div>
                      </div>
                      <button type="button" className="p-2 bg-red-100 text-red-700 rounded-lg opacity-0 group-hover:opacity-100" title="Delete"><FaTrash className="text-sm" /></button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="px-6 py-12 text-center">
                  <Icon className="text-gray-400 text-5xl mx-auto mb-4" />
                  <p className="text-gray-600 font-medium">No {tab.name.toLowerCase()} yet</p>
                  <button type="button" onClick={() => addSett(tab.addFunc)} className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-950 text-white rounded-lg text-sm font-medium">
                    <FaPlus /><span>Add {tab.name.slice(0, -1)}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4 min-h-0">
      <div className="bg-gradient-to-r from-blue-950 to-blue-800 rounded-2xl p-5 text-white shadow-lg shrink-0">
        <div className="flex items-center gap-4">
          <div className="bg-white/20 rounded-xl p-3"><FaCogs className="text-2xl" /></div>
          <div>
            <h1 className="text-xl md:text-2xl font-bold">System Settings</h1>
            <p className="text-blue-100 text-sm">Workflow, billing, SMS, and master data</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col min-h-0 flex-1">
        <div className="flex flex-wrap gap-2 p-3 border-b border-gray-200 bg-gray-50 shrink-0">
          {mainSections.map((section) => {
            const Icon = section.icon;
            const active = mainSection === section.id;
            return (
              <button
                key={section.id}
                type="button"
                onClick={() => setMainSection(section.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  active
                    ? 'bg-gradient-to-r from-blue-950 to-blue-800 text-white shadow-md'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                <Icon className={active ? 'text-white' : 'text-gray-500'} />
                {section.label}
              </button>
            );
          })}
        </div>

        <div className="flex-1 min-h-0 overflow-hidden">
          {mainSection === 'workflow' && renderWorkflowSection()}
          {mainSection === 'billing' && renderBillingSection()}
          {mainSection === 'sms' && renderSmsSection()}
          {mainSection === 'master' && renderMasterSection()}
        </div>
      </div>

      {modal.open && (
        <Modal title={modal.title} onClose={handleClose}>{modal.body}</Modal>
      )}
    </div>
  );
}
